import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, LogOut, ChevronDown, Shield, Sparkles, CalendarClock, Settings, Receipt, Gift, Key, Bell, UserCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../utils/dateFormat';
import { formatTierLabel } from '../constants/subscriptionPlans';

const HOME_URL = import.meta.env.VITE_HOME_URL || 'https://etherworks.cn';

// Utility function to mask email for display (reused from Sidebar)
const maskEmail = (email: string): string => {
    const [localPart, domain] = email.split('@');
    if (!domain) return email;

    let processedDomain = domain;
    if (domain.length > 15) {
        const parts = domain.split('.');
        if (parts.length > 1) {
            const tld = parts[parts.length - 1];
            const mainDomain = parts[parts.length - 2];
            if (mainDomain && mainDomain.length > 8) {
                processedDomain = `${mainDomain.substring(0, 6)}...${tld}`;
            } else {
                processedDomain = `${mainDomain}.${tld}`;
            }
        } else if (domain.length > 12) {
            processedDomain = `${domain.substring(0, 9)}...`;
        }
    }

    const totalLength = localPart.length + processedDomain.length + 1;
    if (totalLength > 20) {
        return `${localPart.charAt(0)}***@${processedDomain}`;
    }

    if (localPart.length > 8) {
        return `${localPart.charAt(0)}***@${processedDomain}`;
    }

    if (localPart.length <= 4) {
        return `${localPart}@${processedDomain}`;
    }

    const firstTwo = localPart.slice(0, 2);
    const lastTwo = localPart.slice(-2);
    return `${firstTwo}***${lastTwo}@${processedDomain}`;
};

interface UserCenterProps {
    className?: string;
    variant?: 'default' | 'sidebar' | 'icon';
}

export default function UserCenter({ className, variant = 'default' }: UserCenterProps) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation('common');
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    if (!user) return null;

    const subscription = user.subscription;
    const tier = subscription?.tier || 'free';
    const isPaidTier = ['plus', 'max', 'ultra'].includes(tier);
    const expiresAt = subscription?.expiresAt;
    const expirationDate = expiresAt ? new Date(expiresAt) : null;
    const expirationDateString = expirationDate
        ? formatDate(expirationDate)
        : null;
    const daysRemaining = expirationDate
        ? Math.max(0, Math.ceil((expirationDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
        : null;
    const tierLabel = formatTierLabel(tier);
    const balanceLabel = `$${(user.balance ?? 0).toFixed(2)}`;

    // Sidebar variant: compact inline display
    if (variant === 'sidebar') {
        return (
            <div className={`flex items-center justify-between ${className || ''}`}>
                <div className="flex items-center gap-2.5 min-w-0">
                    <div className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <User className="h-3.5 w-3.5" />
                        <div className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-green-500 rounded-full border-[1.5px] border-sidebar"></div>
                    </div>
                    <div className="flex flex-col min-w-0">
                        <span className="text-[12px] font-medium text-foreground truncate">
                            {user.email ? maskEmail(user.email) : t('user')}
                        </span>
                        <span className="text-[10px] text-muted-foreground font-medium">
                            {tierLabel}
                        </span>
                    </div>
                </div>
                <a
                    href={`${HOME_URL}/settings`}
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                >
                    <Settings className="h-3.5 w-3.5" />
                </a>
            </div>
        );
    }

    // Icon variant: compact circle button with dropdown
    if (variant === 'icon') {
        return (
            <div className={`relative ${className || ''}`} ref={menuRef}>
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="flex h-10 w-10 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                >
                    <div className="relative flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <User className="h-3.5 w-3.5" />
                        <div className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-green-500 rounded-full border-[1.5px] border-background"></div>
                    </div>
                </button>

                {isOpen && (
                    <div className="absolute left-full bottom-0 ml-1.5 w-[204px] macos-menu animate-in fade-in zoom-in-95 duration-200 z-50">
                        <div className="p-2 border-b border-border/30">
                            <p className="text-[11px] font-medium text-muted-foreground mb-0.5">{t('user_center.signed_in_as')}</p>
                            <p className="text-[13px] font-semibold text-foreground truncate" title={user.email}>
                                {user.email}
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide ${
                                    tier === 'ultra'
                                        ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300'
                                        : tier === 'plus'
                                        ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300'
                                        : tier === 'max'
                                        ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300'
                                        : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                                }`}>
                                    <Sparkles className="h-3 w-3" />
                                    {tierLabel}
                                </span>
                                {isPaidTier && expirationDateString && (
                                    <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                                        <CalendarClock className="h-3 w-3" />
                                        {daysRemaining !== null && daysRemaining <= 7 ? (
                                            <span className="text-red-500 dark:text-red-400 font-medium">
                                                {daysRemaining === 0 ? t('user_center.expires_today') : t('user_center.days_left', { count: daysRemaining })}
                                            </span>
                                        ) : (
                                            <span>{expirationDateString}</span>
                                        )}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="p-1">
                            <Link
                                to="/console/account"
                                className="flex items-center space-x-2 px-2 py-1.5 text-[13px] text-foreground hover:bg-primary/10 rounded-md transition-colors"
                                onClick={() => setIsOpen(false)}
                            >
                                <UserCircle className="h-3.5 w-3.5" />
                                <span>{t('settings:nav.account')}</span>
                            </Link>
                            <Link
                                to="/console/api"
                                className="flex items-center space-x-2 px-2 py-1.5 text-[13px] text-foreground hover:bg-primary/10 rounded-md transition-colors"
                                onClick={() => setIsOpen(false)}
                            >
                                <Key className="h-3.5 w-3.5" />
                                <span>{t('settings:nav.api_keys')}</span>
                            </Link>
                            <Link
                                to="/console"
                                className="flex items-center space-x-2 px-2 py-1.5 text-[13px] text-foreground hover:bg-primary/10 rounded-md transition-colors"
                                onClick={() => setIsOpen(false)}
                            >
                                <Sparkles className="h-3.5 w-3.5" />
                                <span>{t('settings:nav.subscription')}</span>
                            </Link>
                            <Link
                                to="/console/billing-history"
                                className="flex items-center space-x-2 px-2 py-1.5 text-[13px] text-foreground hover:bg-primary/10 rounded-md transition-colors"
                                onClick={() => setIsOpen(false)}
                            >
                                <Receipt className="h-3.5 w-3.5" />
                                <span>{t('settings:nav.billing_history')}</span>
                            </Link>
                            <Link
                                to="/console/giftcard"
                                className="flex items-center space-x-2 px-2 py-1.5 text-[13px] text-foreground hover:bg-primary/10 rounded-md transition-colors"
                                onClick={() => setIsOpen(false)}
                            >
                                <Gift className="h-3.5 w-3.5" />
                                <span>{t('settings:nav.gift_card')}</span>
                            </Link>
                            <Link
                                to="/console/notifications"
                                className="flex items-center space-x-2 px-2 py-1.5 text-[13px] text-foreground hover:bg-primary/10 rounded-md transition-colors"
                                onClick={() => setIsOpen(false)}
                            >
                                <Bell className="h-3.5 w-3.5" />
                                <span>{t('settings:nav.notifications')}</span>
                            </Link>
                            <div className="h-px bg-border/30 my-0.5"></div>
                            <button
                                onClick={() => {
                                    logout();
                                    setIsOpen(false);
                                    navigate('/');
                                }}
                                className="flex items-center space-x-2 px-2 py-1.5 text-[13px] text-destructive hover:bg-destructive/10 rounded-md transition-colors w-full text-left"
                            >
                                <LogOut className="h-3.5 w-3.5" />
                                <span>{t('sign_out')}</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className={`relative ${className || ''}`} ref={menuRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center space-x-2.5 rounded-lg border border-border bg-card p-1 pr-2.5 hover:bg-accent transition-colors shadow-xs"
            >
                <div className="relative flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <User className="h-3.5 w-3.5" />
                    <div className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-green-500 rounded-full border-[1.5px] border-background"></div>
                </div>

                <div className="flex flex-col items-start">
                    <span className="text-[12px] font-medium text-foreground max-w-[120px] truncate">
                        {user.email ? maskEmail(user.email) : t('user')}
                    </span>
                    <div className="text-[10px] text-muted-foreground font-medium tracking-wide flex items-center gap-1.5">
                        <span>{tierLabel}</span>
                        <span className="text-foreground/70">·</span>
                        <span className="text-primary font-semibold">{balanceLabel}</span>
                    </div>
                </div>

                <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute right-0 top-full mt-1.5 w-[204px] macos-menu animate-in fade-in zoom-in-95 duration-200 z-50">
                    <div className="p-2 border-b border-border/30">
                        <p className="text-[11px] font-medium text-muted-foreground mb-0.5">{t('user_center.signed_in_as')}</p>
                        <p className="text-[13px] font-semibold text-foreground truncate" title={user.email}>
                            {user.email}
                        </p>
                        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide ${
                                tier === 'ultra'
                                    ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300'
                                    : tier === 'plus'
                                    ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300'
                                    : tier === 'max'
                                    ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300'
                                    : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                            }`}>
                                <Sparkles className="h-3 w-3" />
                                {tierLabel}
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide bg-primary/10 text-primary">
                                {balanceLabel}
                                <span className="text-[9px] font-medium opacity-70">{t('settings:account.balance_currency')}</span>
                            </span>
                            {isPaidTier && expirationDateString && (
                                <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                                    <CalendarClock className="h-3 w-3" />
                                    {daysRemaining !== null && daysRemaining <= 7 ? (
                                        <span className="text-red-500 dark:text-red-400 font-medium">
                                            {daysRemaining === 0 ? t('user_center.expires_today') : t('user_center.days_left', { count: daysRemaining })}
                                        </span>
                                    ) : (
                                        <span>{expirationDateString}</span>
                                    )}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="p-1">
                        {(user.role === 'admin') && (
                            <>
                                <Link
                                    to="/dashboard"
                                    className="flex items-center space-x-2 px-2 py-1.5 text-[13px] text-primary hover:bg-primary/10 rounded-md transition-colors font-medium"
                                    onClick={() => setIsOpen(false)}
                                >
                                    <Shield className="h-3.5 w-3.5" />
                                    <span>{t('admin_dashboard')}</span>
                                </Link>
                                <div className="h-px bg-border/30 my-0.5"></div>
                            </>
                        )}

                        <Link
                            to="/console/account"
                            className="flex items-center space-x-2 px-2 py-1.5 text-[13px] text-foreground hover:bg-primary/10 rounded-md transition-colors"
                            onClick={() => setIsOpen(false)}
                        >
                            <UserCircle className="h-3.5 w-3.5" />
                            <span>{t('settings:nav.account')}</span>
                        </Link>
                        <Link
                            to="/console/api"
                            className="flex items-center space-x-2 px-2 py-1.5 text-[13px] text-foreground hover:bg-primary/10 rounded-md transition-colors"
                            onClick={() => setIsOpen(false)}
                        >
                            <Key className="h-3.5 w-3.5" />
                            <span>{t('settings:nav.api_keys')}</span>
                        </Link>
                        <Link
                            to="/console"
                            className="flex items-center space-x-2 px-2 py-1.5 text-[13px] text-foreground hover:bg-primary/10 rounded-md transition-colors"
                            onClick={() => setIsOpen(false)}
                        >
                            <Sparkles className="h-3.5 w-3.5" />
                            <span>{t('settings:nav.subscription')}</span>
                        </Link>
                        <Link
                            to="/console/billing-history"
                            className="flex items-center space-x-2 px-2 py-1.5 text-[13px] text-foreground hover:bg-primary/10 rounded-md transition-colors"
                            onClick={() => setIsOpen(false)}
                        >
                            <Receipt className="h-3.5 w-3.5" />
                            <span>{t('settings:nav.billing_history')}</span>
                        </Link>
                        <Link
                            to="/console/giftcard"
                            className="flex items-center space-x-2 px-2 py-1.5 text-[13px] text-foreground hover:bg-primary/10 rounded-md transition-colors"
                            onClick={() => setIsOpen(false)}
                        >
                            <Gift className="h-3.5 w-3.5" />
                            <span>{t('settings:nav.gift_card')}</span>
                        </Link>
                        <div className="h-px bg-border/30 my-0.5"></div>

                        <button
                            onClick={() => {
                                logout();
                                setIsOpen(false);
                                navigate('/');
                            }}
                            className="flex items-center space-x-2 px-2 py-1.5 text-[13px] text-destructive hover:bg-destructive/10 rounded-md transition-colors w-full text-left"
                        >
                            <LogOut className="h-3.5 w-3.5" />
                            <span>{t('sign_out')}</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
